import { MI_NODES } from '~/widgets/CostBomDemo/assets/generated/miNodes'
import { PANELS_HTML } from '~/widgets/CostBomDemo/assets/generated/panelsHtml'
import { SKELETON_HTML } from '~/widgets/CostBomDemo/assets/generated/skeletonHtml'
import {
  mountCostSummary,
  mountCostSummaryIdle,
} from '~/widgets/CostBomDemo/engine/mountCostSummary'
import { initHeroSampleBreakdown } from '~/widgets/CostBomDemo/source/hero-sample/breakdown'
import { initHeroSampleFormat } from '~/widgets/CostBomDemo/source/hero-sample/format'
import { initHeroSampleIdle } from '~/widgets/CostBomDemo/source/hero-sample/idle'
import { initHeroSampleInit } from '~/widgets/CostBomDemo/source/hero-sample/init'

let panelsReady = false
let panelsLoading: Promise<void> | null = null

function ensurePanelsReady(root: HTMLElement): Promise<void> {
  if (panelsReady) return Promise.resolve()
  if (panelsLoading) return panelsLoading

  panelsLoading = new Promise((resolve) => {
    const sampleRoot = root.querySelector('#hero-sample-root')
    if (!sampleRoot) {
      resolve()
      return
    }

    const wrap = document.createElement('div')
    wrap.innerHTML = PANELS_HTML
    while (wrap.firstChild) sampleRoot.appendChild(wrap.firstChild)

    initHeroSampleFormat()
    mountCostSummary(sampleRoot)
    initHeroSampleBreakdown()
    initHeroSampleInit()

    panelsReady = true
    window.heroPanelsReady = true
    resolve()
  })

  return panelsLoading
}

/**
 * Forgets that the panels were inserted.
 *
 * `mountBomPanels` replaces the sample root with the skeleton, so a widget
 * mounted again (React StrictMode in development, or the element leaving and
 * rejoining the page) has lost its panels. Without this, the cached readiness
 * skips re-inserting them and the model finishes into an empty card.
 */
export function resetBomPanels() {
  panelsReady = false
  panelsLoading = null
  window.heroPanelsReady = false
}

export function mountBomPanels(root: HTMLElement) {
  window.MI_NODES = MI_NODES

  const sampleRoot = root.querySelector('#hero-sample-root')
  if (!sampleRoot) return

  sampleRoot.innerHTML = SKELETON_HTML
  mountCostSummaryIdle(sampleRoot)
  initHeroSampleIdle()

  const sampleMap: Record<string, string> = {
    'NMC Battery Cell': 'nmc',
    'Citric Acid': 'citric',
    'Smart Home SoC': 'soc',
  }

  window.heroGenerateSample = (key?: string) => {
    const pin = root.querySelector<HTMLInputElement>('#hero-dd-product')
    const sampleKey =
      key || sampleMap[pin?.value || 'NMC Battery Cell'] || 'nmc'
    return ensurePanelsReady(root).then(() => {
      if (window.heroShowSample) window.heroShowSample(sampleKey)
    })
  }

  window.heroShowIdle = () => {
    if (window.heroShowSample) window.heroShowSample('idle')
  }
}

/**
 * Drops the skeleton state a freshly shown sample carries.
 *
 * `revealLoading` in the legacy sample init holds every table in
 * `mi-bom--loading` for 1.1s, which reads as work happening while a generation
 * runs. Showing a pre-rendered sample is not that: nothing is being computed,
 * so the delay is the visitor waiting on nothing, and it costs the demo its
 * whole point of opening on real output.
 */
export function settleSample(root: HTMLElement) {
  root
    .querySelectorAll('#cm2-panels .mi-bom--table.mi-bom--loading')
    .forEach((table) => table.classList.remove('mi-bom--loading'))
}

/**
 * Plays the demo's own generation once on load, so a visitor sees the model
 * being built instead of an empty frame. It does not scroll: the visitor has
 * not asked for anything yet.
 */
export function bootGenerate(root: HTMLElement): Promise<void> {
  return ensurePanelsReady(root).then(() => {
    window.heroStartGenerate?.({ scroll: false })
  })
}

declare global {
  interface Window {
    MI_NODES?: Record<
      string,
      {
        name: string
        cost: number
        mass: number
        profile: string
        sources: Array<{ n: string; p: number }>
        rootCost: number
        breakdown?: Record<string, number>
      }
    >
    heroPanelsReady?: boolean
    heroGenerateSample?: (key?: string) => Promise<void>
    heroShowIdle?: () => void
    heroShowSample?: (key: string) => void
    heroStartGenerate?: (opts?: { scroll?: boolean }) => void
    cm2NoScroll?: boolean
    cm2BuildTab?: (product: string, country: string) => void
    closeHeroTabAddPop?: () => void
    setHeroProductImage?: (product: string) => void
  }
}
